import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { abTrack, useLandingImages } from "@/lib/ab";
import { LOGO_FULL } from "@/components/common";
import { COPY } from "./landing/copy";
import { CheckoutModal } from "./landing/Checkout";
import { FREE_COPY, HERO_IMG, PREVIEW_IMG } from "./freelanding/copy";
import { SignupSheet } from "./freelanding/SignupSheet";
import { FHero, FProblem, FGet, FPreview, FMore, FTrust, FFinal } from "./freelanding/Sections";

const LANG_KEY = "sb_free_lang";
const VARIANT = "free_new";

function LangSwitch({ lang, setLang }) {
  return (
    <div className="flex rounded-full border border-[#D5E3E8] p-0.5 text-[13px] font-bold" role="group" aria-label="Language" data-testid="free-lang-switch">
      {["en", "fr"].map((l) => <button key={l} onClick={() => setLang(l)} aria-pressed={lang === l} className={`px-3.5 h-9 rounded-full ${lang === l ? "bg-[#102B3F] text-white" : "text-[#5C6B73]"}`} data-testid={`free-lang-${l}`}>{FREE_COPY[l].langName}</button>)}
    </div>
  );
}

export default function FreeLanding() {
  const [lang, setLangState] = useState(() => localStorage.getItem(LANG_KEY) || "en");
  const [offer, setOffer] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [trust, setTrust] = useState(null);
  const [signup, setSignup] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [sticky, setSticky] = useState(false);
  const t = FREE_COPY[lang];
  const imgs = useLandingImages();
  const ev = (event, extra = {}) => abTrack(event, { lang, variant: VARIANT, ...extra });
  const setLang = (l) => { if (l === lang) return; localStorage.setItem(LANG_KEY, l); setLangState(l); abTrack("language_changed", { lang: l, variant: VARIANT }); };
  useEffect(() => {
    abTrack("landing_page_view", { lang, variant: VARIANT });
    api.get("/public/offer").then((r) => setOffer(r.data)).catch(() => {});
    api.get("/public/sample-ideas").then((r) => setIdeas(r.data.filter((i) => Number(i.number) !== 20).slice(0, 5))).catch(() => {});
    api.get("/public/trust").then((r) => setTrust(r.data)).catch(() => {});
    const onScroll = () => setSticky(window.scrollY > 520);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const cta = (where) => { ev("main_cta_click", { props: { cta: where } }); ev("signup_started"); setSignup(true); };
  const unlock = () => { ev("main_cta_click", { props: { cta: "unlock" } }); setCheckout(true); };
  return (
    <div className="min-h-screen bg-white overflow-x-hidden pb-24 md:pb-0" data-testid="free-landing-page">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#E1ECEF] pt-safe">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 sm:h-16 flex items-center justify-between gap-3">
          <img src={LOGO_FULL} alt="Sidebizz" className="h-7 sm:h-8" data-testid="free-landing-logo" />
          <LangSwitch lang={lang} setLang={setLang} />
        </div>
      </header>
      <FHero t={t} cta={cta} img={imgs && (imgs.free_hero || HERO_IMG)} />
      <FProblem t={t} />
      <FGet t={t} cta={cta} />
      <FPreview t={t} cta={cta} img={imgs && (imgs.free_preview || PREVIEW_IMG)} />
      <FMore t={t} ideas={ideas} offer={offer} unlock={unlock} lang={lang} />
      <FTrust t={t} trust={trust} />
      <FFinal t={t} cta={cta} />
      {sticky && !signup && !checkout && (
        <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-[#E1ECEF] px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]" data-testid="free-sticky-bar">
          <button onClick={() => cta("sticky")} className="lp-btn w-full min-h-[52px]" data-testid="free-sticky-cta">{t.sticky} <ArrowRight className="h-5 w-5" /></button>
        </div>
      )}
      <SignupSheet open={signup} onOpenChange={setSignup} a={t.auth} lang={lang} />
      <CheckoutModal open={checkout} onOpenChange={setCheckout} t={COPY[lang]} lang={lang} />
    </div>
  );
}
